"use client";

import { useMemo, useState, type ReactNode } from "react";
import { Input } from "@velpha/ui/components/input";
import { cn } from "@velpha/ui/lib/utils";
import { ArrowDownAZ, ArrowUpAZ, Search, X } from "lucide-react";
import { useDocLocale } from "@/components/shared/docs-blocks";
import type { Locale } from "@/lib/i18n";

const STRINGS: Record<
  Locale,
  {
    count: (shown: number, total: number) => string;
    searchPlaceholder: string;
    clearSearch: string;
    allChapters: string;
    allKinds: string;
    term: string;
    scope: string;
    kind: string;
    meaning: string;
    noMatch: string;
  }
> = {
  ja: {
    count: (shown, total) => `${shown} / ${total} 件`,
    searchPlaceholder: "用語を検索",
    clearSearch: "検索をクリア",
    allChapters: "全範囲",
    allKinds: "すべて",
    term: "用語",
    scope: "範囲",
    kind: "分類",
    meaning: "意味",
    noMatch: "該当する用語が見つかりませんでした",
  },
  en: {
    count: (shown, total) => `${shown} / ${total}`,
    searchPlaceholder: "Search terms",
    clearSearch: "Clear search",
    allChapters: "All scopes",
    allKinds: "All",
    term: "Term",
    scope: "Scope",
    kind: "Category",
    meaning: "Meaning",
    noMatch: "No matching terms found",
  },
};

type DocGlossaryRow = {
  term: string;
  chapter?: string;
  kind: string;
  summary: string;
  pages?: string;
};

// 分類名は MDX から来るので、日本語と英語の両方のキーを持たせる。
const KIND_BADGE_CLASS: Record<string, string> = {
  前提: "border-zinc-500/30 bg-zinc-500/10 text-zinc-700 dark:text-zinc-300",
  Foundations: "border-zinc-500/30 bg-zinc-500/10 text-zinc-700 dark:text-zinc-300",
  商品: "border-slate-500/30 bg-slate-500/10 text-slate-700 dark:text-slate-300",
  Products: "border-slate-500/30 bg-slate-500/10 text-slate-700 dark:text-slate-300",
  株式: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  Equities: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  投資信託: "border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-400",
  Funds: "border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-400",
  ポートフォリオ: "border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-400",
  Portfolio: "border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-400",
  "指標・式": "border-cyan-500/30 bg-cyan-500/10 text-cyan-700 dark:text-cyan-400",
  "Metrics & formulas": "border-cyan-500/30 bg-cyan-500/10 text-cyan-700 dark:text-cyan-400",
  モデル: "border-indigo-500/30 bg-indigo-500/10 text-indigo-700 dark:text-indigo-400",
  Models: "border-indigo-500/30 bg-indigo-500/10 text-indigo-700 dark:text-indigo-400",
  制度: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400",
  Accounts: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400",
};

const KIND_ORDER = [
  "前提",
  "Foundations",
  "商品",
  "Products",
  "株式",
  "Equities",
  "投資信託",
  "Funds",
  "ポートフォリオ",
  "Portfolio",
  "指標・式",
  "Metrics & formulas",
  "モデル",
  "Models",
  "制度",
  "Accounts",
];

const CHAPTER_BADGE_CLASS: Record<string, string> = {
  第1章: "border-border bg-background text-muted-foreground",
  第2章: "border-border bg-background text-muted-foreground",
  第3章: "border-border bg-background text-muted-foreground",
  第4章: "border-border bg-background text-muted-foreground",
  発展: "border-border bg-background text-muted-foreground",
  "Chapter 1": "border-border bg-background text-muted-foreground",
  "Chapter 2": "border-border bg-background text-muted-foreground",
  "Chapter 3": "border-border bg-background text-muted-foreground",
  "Chapter 4": "border-border bg-background text-muted-foreground",
  Advanced: "border-border bg-background text-muted-foreground",
};

type SortKey = "chapter" | "kind" | "term";

function makeOrder(rows: DocGlossaryRow[], field: "chapter" | "kind") {
  const seen = new Map<string, number>();
  if (field === "kind") {
    KIND_ORDER.forEach((kind, index) => {
      seen.set(kind, index);
    });
  }
  rows.forEach((row, index) => {
    const value = row[field];
    if (value && !seen.has(value)) seen.set(value, index);
  });
  return seen;
}

export function DocGlossaryTable({ title, rows }: { title?: ReactNode; rows: DocGlossaryRow[] }) {
  const t = STRINGS[useDocLocale()];
  const [query, setQuery] = useState("");
  const [activeChapter, setActiveChapter] = useState<string | null>(null);
  const [activeKind, setActiveKind] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>("chapter");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const normalizedQuery = query.trim().toLowerCase();
  const hasChapterRows = rows.some((row) => row.chapter);

  const chapterOrder = useMemo(() => {
    return makeOrder(rows, "chapter");
  }, [rows]);

  const kindOrder = useMemo(() => {
    return makeOrder(rows, "kind");
  }, [rows]);

  const chapterCounts = useMemo(() => {
    const map = new Map<string, number>();
    for (const row of rows) {
      if (row.chapter) map.set(row.chapter, (map.get(row.chapter) ?? 0) + 1);
    }
    return Array.from(map.entries());
  }, [rows]);

  const kindCounts = useMemo(() => {
    const map = new Map<string, number>();
    for (const row of rows) map.set(row.kind, (map.get(row.kind) ?? 0) + 1);
    return Array.from(map.entries()).sort(([a], [b]) => (kindOrder.get(a) ?? 999) - (kindOrder.get(b) ?? 999));
  }, [rows, kindOrder]);

  const filteredRows = useMemo(() => {
    let base = rows;
    if (activeChapter) base = base.filter((r) => r.chapter === activeChapter);
    if (activeKind) base = base.filter((r) => r.kind === activeKind);
    if (normalizedQuery) {
      base = base.filter((row) => {
        const haystack = [row.term, row.chapter, row.kind, row.pages, row.summary].join(" ").toLowerCase();
        return haystack.includes(normalizedQuery);
      });
    }

    const byChapter = (a: DocGlossaryRow, b: DocGlossaryRow) => {
      const ai = chapterOrder.get(a.chapter ?? "") ?? 0;
      const bi = chapterOrder.get(b.chapter ?? "") ?? 0;
      return ai - bi;
    };
    const byKind = (a: DocGlossaryRow, b: DocGlossaryRow) => {
      const ai = kindOrder.get(a.kind) ?? 0;
      const bi = kindOrder.get(b.kind) ?? 0;
      return ai - bi;
    };
    const byTerm = (a: DocGlossaryRow, b: DocGlossaryRow) => a.term.localeCompare(b.term, "ja");

    return [...base].sort((a, b) => {
      let diff =
        sortKey === "chapter"
          ? byChapter(a, b) || byKind(a, b) || byTerm(a, b)
          : sortKey === "kind"
            ? byKind(a, b) || byChapter(a, b) || byTerm(a, b)
            : byTerm(a, b);
      if (sortDir === "desc") diff = -diff;
      return diff;
    });
  }, [normalizedQuery, activeChapter, activeKind, rows, sortKey, sortDir, chapterOrder, kindOrder]);

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  return (
    <div className="space-y-4">
      {/* Header: title + search */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          {title ? <div className="text-sm font-semibold tracking-tight">{title}</div> : null}
          <div className="mt-0.5 text-xs text-muted-foreground">
            {t.count(filteredRows.length, rows.length)}
          </div>
        </div>
        <div className="relative w-full sm:max-w-xs">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t.searchPlaceholder}
            className="h-9 pl-8 pr-8 text-sm"
            aria-label={t.searchPlaceholder}
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label={t.clearSearch}
            >
              <X className="size-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Chapter filter chips */}
      {hasChapterRows ? (
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            onClick={() => setActiveChapter(null)}
            className={cn(
              "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors",
              activeChapter === null
                ? "border-foreground bg-foreground text-background"
                : "border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground"
            )}
          >
            {t.allChapters}
            <span className="ml-1 text-[10px] opacity-70">{rows.length}</span>
          </button>
          {chapterCounts.map(([chapter, count]) => (
            <button
              key={chapter}
              onClick={() => setActiveChapter((prev) => (prev === chapter ? null : chapter))}
              className={cn(
                "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors",
                activeChapter === chapter
                  ? "border-foreground bg-muted text-foreground font-semibold"
                  : "border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
            >
              {chapter}
              <span className="ml-1 text-[10px] opacity-70">{count}</span>
            </button>
          ))}
        </div>
      ) : null}

      {/* Kind filter chips */}
      <div className="flex flex-wrap items-center gap-1.5">
        <button
          onClick={() => setActiveKind(null)}
          className={cn(
            "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors",
            activeKind === null
              ? "border-foreground bg-foreground text-background"
              : "border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground"
          )}
        >
          {t.allKinds}
          <span className="ml-1 text-[10px] opacity-70">{rows.length}</span>
        </button>
        {kindCounts.map(([kind, count]) => (
          <button
            key={kind}
            onClick={() => setActiveKind((prev) => (prev === kind ? null : kind))}
            className={cn(
              "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors",
              activeKind === kind
                ? cn("font-semibold", KIND_BADGE_CLASS[kind] ?? "border-border bg-muted text-foreground")
                : "border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground"
            )}
          >
            {kind}
            <span className="ml-1 text-[10px] opacity-70">{count}</span>
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr className="border-b border-border">
              <th className="w-[28%] px-4 py-2.5 text-left sm:w-auto">
                <button
                  onClick={() => handleSort("term")}
                  className={cn(
                    "inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider hover:text-foreground",
                    sortKey === "term" ? "text-foreground" : "text-muted-foreground"
                  )}
                >
                  {t.term}
                  {sortKey === "term" ? (
                    sortDir === "asc" ? (
                      <ArrowUpAZ className="size-3.5" />
                    ) : (
                      <ArrowDownAZ className="size-3.5" />
                    )
                  ) : null}
                </button>
              </th>
              {hasChapterRows ? (
                <th className="hidden px-3 py-2.5 text-left md:table-cell">
                  <button
                    onClick={() => handleSort("chapter")}
                    className={cn(
                      "inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider hover:text-foreground",
                      sortKey === "chapter" ? "text-foreground" : "text-muted-foreground"
                    )}
                  >
                    {t.scope}
                    {sortKey === "chapter" ? (
                      sortDir === "asc" ? (
                        <ArrowUpAZ className="size-3.5" />
                      ) : (
                        <ArrowDownAZ className="size-3.5" />
                      )
                    ) : null}
                  </button>
                </th>
              ) : null}
              <th className="hidden px-3 py-2.5 text-left sm:table-cell">
                <button
                  onClick={() => handleSort("kind")}
                  className={cn(
                    "inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider hover:text-foreground",
                    sortKey === "kind" ? "text-foreground" : "text-muted-foreground"
                  )}
                >
                  {t.kind}
                  {sortKey === "kind" ? (
                    sortDir === "asc" ? (
                      <ArrowUpAZ className="size-3.5" />
                    ) : (
                      <ArrowDownAZ className="size-3.5" />
                    )
                  ) : null}
                </button>
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t.meaning}
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 ? (
              <tr>
                <td colSpan={hasChapterRows ? 4 : 3} className="px-4 py-8 text-center text-sm text-muted-foreground">
                  {t.noMatch}
                </td>
              </tr>
            ) : (
              filteredRows.map((row) => (
                <tr
                  key={`${row.chapter ?? "all"}-${row.kind}-${row.term}`}
                  className="border-b border-border last:border-b-0 hover:bg-muted/30 transition-colors"
                >
                  <td className="px-4 py-3 align-top">
                    <div className="flex flex-col gap-1 sm:block">
                      <span className="text-sm font-medium text-foreground">{row.term}</span>
                      <div className="flex flex-wrap gap-1 sm:hidden">
                        {row.chapter ? (
                          <span
                            className={cn(
                              "inline-flex w-fit items-center rounded-full border px-1.5 py-0.5 text-[10px] font-semibold",
                              CHAPTER_BADGE_CLASS[row.chapter] ?? "border-border bg-muted text-foreground"
                            )}
                          >
                            {row.chapter}
                          </span>
                        ) : null}
                        <span
                          className={cn(
                            "inline-flex w-fit items-center rounded-full border px-1.5 py-0.5 text-[10px] font-semibold",
                            KIND_BADGE_CLASS[row.kind] ?? "border-border bg-muted text-foreground"
                          )}
                        >
                          {row.kind}
                        </span>
                      </div>
                    </div>
                  </td>
                  {hasChapterRows ? (
                    <td className="hidden whitespace-nowrap px-3 py-3 align-top md:table-cell">
                      {row.chapter ? (
                        <div className="space-y-1">
                          <span
                            className={cn(
                              "inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold",
                              CHAPTER_BADGE_CLASS[row.chapter] ?? "border-border bg-muted text-foreground"
                            )}
                          >
                            {row.chapter}
                          </span>
                          {row.pages ? <div className="text-xs text-muted-foreground">{row.pages}</div> : null}
                        </div>
                      ) : null}
                    </td>
                  ) : null}
                  <td className="hidden whitespace-nowrap px-3 py-3 align-top sm:table-cell">
                    <span
                      className={cn(
                        "inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold",
                        KIND_BADGE_CLASS[row.kind] ?? "border-border bg-muted text-foreground"
                      )}
                    >
                      {row.kind}
                    </span>
                  </td>
                  <td className="px-4 py-3 align-top text-sm leading-6 text-muted-foreground">{row.summary}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
